import { useState, useEffect, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface ValidatedSnItemResponse {
  success: boolean;
  data?: Array<{ inventoryItemId: number; segment1: string; description: string; validatedAssetNumber: string }>;
  error?: string;
}

interface ValidatedSnItemParam {
  serial_number: string;
}

export const useValidatedSnItem = (
  serialNumber?: string,
  executeApiOnload = false
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [data, setData] = useState<{itemNumber: string; description: string; validatedAssetNumber: string}>({ itemNumber: "", description: "", validatedAssetNumber: "" });

  const fetchData = async (
    params: ValidatedSnItemParam,
    callBackFn?: (args?: {itemNumber: string; description: string; validatedAssetNumber: string}) => void
  ) => {
    setIsLoading(true);
    setData({ itemNumber: "", description: "", validatedAssetNumber: "" });

    try {
      if (!params.serial_number?.trim()) {
        callBackFn && callBackFn({ itemNumber: "", description: "", validatedAssetNumber: "" });
        return;
      }

      const response = await apiClient.get<ValidatedSnItemResponse>(
        API_CONFIG.ENDPOINTS.GET_VALIDATED_SN_ITEM,
        {
          params: {
            serial_number: String(params.serial_number || '').trim(),
          },
        }
      );

      let parsedData = { itemNumber: "", description: "", validatedAssetNumber: "" };
      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        parsedData = {
          itemNumber: response.data.data[0].segment1 || "",
          description: response.data.data[0].description || "",
          validatedAssetNumber: response.data.data[0].validatedAssetNumber || ""
        };
      }

      setData(parsedData);
      callBackFn && callBackFn(parsedData);
    } catch (error: any) {
      console.error(error);
      setError(error);
      callBackFn && callBackFn({ itemNumber: "", description: "", validatedAssetNumber: "" });
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = (serialNum: string, callBackFn?: (args?: {itemNumber: string; description: string; validatedAssetNumber: string}) => void) => {
    fetchData({ serial_number: serialNum }, callBackFn);
  };

  const fetchValidatedSnItem = useCallback(async (serialNum: string): Promise<{itemNumber: string; description: string; validatedAssetNumber: string} | null> => {
    try {
      if (!serialNum?.trim()) {
        return null;
      }

      const response = await apiClient.get<ValidatedSnItemResponse>(
        API_CONFIG.ENDPOINTS.GET_VALIDATED_SN_ITEM,
        {
          params: {
            serial_number: String(serialNum || '').trim(),
          },
        }
      );

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        return {
          itemNumber: response.data.data[0].segment1 || "",
          description: response.data.data[0].description || "",
          validatedAssetNumber: response.data.data[0].validatedAssetNumber || ""
        };
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching validated serial item:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (executeApiOnload && serialNumber) {
      fetchData({ serial_number: serialNumber });
    }
  }, [executeApiOnload, serialNumber]);

  return { data, isLoading, error, refetch, fetchValidatedSnItem };
};
